import { useState, useEffect } from 'react';
import { resolveImageUrl } from '@/lib/firebase';
import { Package } from 'lucide-react';

const gradients = [
  'from-rose-200 to-pink-100',
  'from-sky-200 to-blue-100',
  'from-amber-200 to-orange-100',
  'from-emerald-200 to-green-100',
  'from-violet-200 to-purple-100',
  'from-cyan-200 to-teal-100',
];

function getGradient(name) {
  if (!name) return gradients[0];
  const idx = name.charCodeAt(0) % gradients.length;
  return gradients[idx];
}

function getInitials(name) {
  if (!name) return '?';
  const words = name.split(' ').filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

export default function ProductImage({ src, alt, className = '', name = '' }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setUrl(null);

    if (!src) {
      setLoading(false);
      setError(true);
      return;
    }

    // Firebase veya Cloudinary URL'sini çözümler
    resolveImageUrl(src).then((resolved) => {
      if (!cancelled) {
        if (resolved) {
          setUrl(resolved);
        } else {
          setError(true);
        }
        setLoading(false);
      }
    });

    return () => { cancelled = true; };
  }, [src]);

  // Video olup olmadığını kontrol eden yardımcı fonksiyon
  const isVideo = (checkUrl) => {
    if (!checkUrl) return false;
    return (
      checkUrl.match(/\.(mp4|webm|ogg|mov)$/i) || 
      checkUrl.includes('/video/upload/')
    );
  };

  const displayName = alt || name || '';
  const grad = getGradient(displayName);
  const initials = getInitials(displayName);

  // Placeholder component (Görsel veya Video yüklenemezse çalışır)
  const Placeholder = () => (
    <div className={`bg-gradient-to-br ${grad} flex flex-col items-center justify-center gap-2 ${className}`}>
      <div className="w-12 h-12 rounded-xl bg-white/50 backdrop-blur flex items-center justify-center">
        <span className="text-lg font-bold text-brand-dark/50">{initials}</span>
      </div>
      {displayName && (
        <p className="text-xs text-brand-dark/40 font-medium text-center px-4 line-clamp-2 max-w-[80%]">
          {displayName}
        </p>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={`bg-gradient-to-br ${grad} flex items-center justify-center ${className}`}>
        <div className="w-6 h-6 border-2 border-white/30 border-t-white/70 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !url) {
    return <Placeholder />;
  }

  // Eğer URL bir video ise video etiketi bas
  if (isVideo(url)) {
    return (
      <video
        src={url}
        className={`${className} object-cover`}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  }

  // Değilse görsel etiketi bas
  return (
    <img
      src={url}
      alt={alt || 'Ürün görseli'}
      className={className}
      onError={() => setError(true)}
    />
  );
}